import { VariableTokenKind } from "./variable-token.types";

/**
 * The closed set of variable kinds supported by the Ask Spotlight variable token grammar. Declared as a
 * `const` tuple (rather than a TypeScript `enum`) so `VariableTokenKind` can be derived directly from it via
 * `(typeof VARIABLE_KINDS)[number]`, keeping the runtime list and the type in lock-step.
 *
 * Append-only. Kinds are embedded in persisted Ask messages, so removing or renaming one turns every
 * historical pill of that kind back into plain text. Adding a kind also means updating the design doc
 * appendix and the agent system prompt (GAP-1852), neither of which the compiler can check.
 */
export const VARIABLE_KINDS = [
    "agent",
    "agentGroup",
    "tag",
    "reviewer",
    "scorecard",
    "language",
    "metric",
    "emotion",
    "field",
    "timePeriod",
    "template",
] as const;

/** The kind a template token uses. Templates are drawn by the `templateMention` node, never as a variable pill. */
export const TEMPLATE_TOKEN_KIND = "template" satisfies VariableTokenKind;

/**
 * The master regex for an Ask Spotlight variable token, e.g. `{{agent:(agentId=a1,a2)|Agent|Sarah Chen|John Doe}}`.
 *
 * Capture groups:
 *   1. `type`    - one of `VARIABLE_KINDS`.
 *   2. `subtype` - optional, after a `#`, e.g. `metric#handlingTimeSeconds`.
 *   3. `values`  - the raw `;`-separated `key=value[,value…]` pairs inside `(...)`, untrimmed.
 *   4. `text`    - the `|`-separated title and labels, with `\`, `}` and `|` backslash-escaped.
 *
 * The `\s*` around `:` is the only whitespace tolerance in the regex itself.
 *
 * Global (`g`) and shared: only consume it via `matchAll`. Never call `.exec()` or `.test()` on it - both
 * advance its `lastIndex` and corrupt every later match against it.
 */
export const VARIABLE_TOKEN_REGEX = /\{\{(\w+)(?:#(\w+))?\s*:\s*\(([^)]+)\)\|((?:\\.|[^\\}])+)\}\}/g;

/**
 * Whether each kind requires a `#subtype` (`true`) or forbids one (`false`). Keyed by every kind so a new kind
 * without a subtype rule fails to compile. Where required, the subtype says what the values mean:
 * - `metric` / `emotion` / `field`: the name that composes each value key into the real query param
 *   (`minimum` + `handlingTimeSeconds` -> `minimumHandlingTimeSeconds`); without it the filter is unidentifiable.
 * - `tag`: the group's `TagGroupType` (e.g. `AUTOMATIC_TAG`), which picks the pill's icon and tooltip prefix.
 *   Never `AGENT`, as agent groups are the `agentGroup` kind.
 */
export const KIND_HAS_SUBTYPE: Readonly<Record<VariableTokenKind, boolean>> = {
    agent: false,
    agentGroup: false,
    tag: true,
    reviewer: false,
    scorecard: false,
    language: false,
    metric: true,
    emotion: true,
    field: true,
    timePeriod: false,
    template: false,
};

/** Keys and subtypes are word characters only - the same `\w+` the regex enforces. */
export const VARIABLE_KEY_PATTERN = /^\w+$/;

/**
 * Characters a value may never contain because each is token syntax: `)`, `;`, `,`, `=`, `{` and `}`. Values are
 * UUIDs, ISO datetimes, enum names, numbers or booleans, so none is ever needed.
 */
export const DISALLOWED_VALUE_CHARACTERS_PATTERN = /[)=;,{}]/;

/**
 * Keys rejected even though they match `VARIABLE_KEY_PATTERN`. `__proto__` has an inherited setter on
 * `Object.prototype`, so bracket-assigning it would reassign the prototype of the values object instead of
 * adding a property; `constructor` and `prototype` are special-cased for the same reason. None is a
 * legitimate business key.
 */
export const RESERVED_VALUE_KEYS: ReadonlySet<string> = new Set(["__proto__", "constructor", "prototype"]);
